import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Users, BookOpen, Compass, Smile, Video } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function Home() {
  return (
    <div className="overflow-hidden">
      <SEO 
        title="Vejen til barnet | Theraplay, Marte Meo & Pædagogisk Rådgivning"
        description="Styrk relationen til dit barn. Med 25 års erfaring tilbyder jeg Theraplay, Marte Meo og pædagogisk rådgivning. Få hjælp til nærvær og trivsel her."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] md:min-h-[100vh] flex items-center pt-48 md:pt-64 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/c3cb9746-8d8b-4d44-9436-78029021c2f3.png" 
            alt="Hero" 
            className="w-full h-full object-cover object-bottom"
          />
          <div className="absolute inset-0 bg-stone-900/55" />
        </div>

        {/* Wave Divider at bottom of Hero */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-stone-50"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white text-center pb-32 md:pb-48">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto translate-y-8 md:translate-y-12"
          >
            {/* Hero Logo */}
            <div className="flex justify-center mb-12">
              <img 
                src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/47f26c9d-0f3c-41b8-a3e3-33153b18a220.png" 
                alt="Logo" 
                className="w-32 md:w-48 h-auto opacity-[0.8]"
              />
            </div>

            <h1 className="text-4xl md:text-6xl font-light tracking-tight mb-8 uppercase leading-[1.1]">
              Nærvær, Relationer & Udvikling
            </h1>
            <p className="text-xl md:text-3xl text-stone-100 mb-12 leading-relaxed font-light">
              Jeg hjælper forældre og fagfolk med at skabe stærke bånd og trivsel gennem pædagogisk rådgivning, Theraplay og Marte Meo.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#paedagogiske-oplaeg" 
                className="bg-stone-800 hover:bg-stone-900 text-white px-6 py-3.5 rounded-full font-medium transition-all hover:-translate-y-1 text-sm md:text-base border border-stone-700 text-center"
              >
                Pædagogiske oplæg
              </a>
              <Link 
                to="/theraplay" 
                className="bg-stone-800 hover:bg-stone-900 text-white px-6 py-3.5 rounded-full font-medium transition-all hover:-translate-y-1 text-sm md:text-base border border-stone-700 text-center"
              >
                Theraplay
              </Link>
              <Link 
                to="/marte-meo" 
                className="bg-stone-800 hover:bg-stone-900 text-white px-6 py-3.5 rounded-full font-medium transition-all hover:-translate-y-1 text-sm md:text-base border border-stone-700 text-center"
              >
                Marte Meo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview - Moved here */}
      <section className="py-16 pt-0 pb-24 md:pb-32 bg-stone-50 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 pt-8 md:pt-12">
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 mb-6 uppercase">Mine ydelser</h2>
            <p className="text-lg text-stone-600">
              Jeg tilbyder individuelt tilrettelagte forløb, rådgivning og terapi, der tager udgangspunkt i jeres specifikke behov og hverdag.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Pædagogisk rådgivning */}
            <motion.div
              id="paedagogiske-oplaeg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center text-center p-4 scroll-mt-24"
            >
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="w-8 h-8 text-stone-800" />
              </div>
              <h3 className="text-2xl font-light text-stone-900 mb-4 uppercase">Pædagogiske oplæg</h3>
              <p className="text-stone-600 mb-8 leading-relaxed flex-grow">
                Jeg tilbyder praksisnære og faglige oplæg samt workshops, der er personligt tilpasset jeres institution. Med udgangspunkt i jeres hverdag og 25 års erfaring styrker vi relationskompetencen, den pædagogiske struktur og det gode samarbejde i daginstitutioner, skoler og personalegrupper.
              </p>
              <Link to="/oplaeg" className="bg-stone-900 text-white px-8 py-3 rounded-full font-medium inline-block hover:bg-stone-800 transition-all hover:-translate-y-1 w-full">
                Læs mere
              </Link>
            </motion.div>

            {/* Theraplay */}
            <motion.div
              id="theraplay"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex flex-col items-center text-center p-4 scroll-mt-24"
            >
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-6">
                <Smile className="w-8 h-8 text-stone-800" />
              </div>
              <h3 className="text-2xl font-light text-stone-900 mb-4 uppercase">Theraplay</h3>
              <p className="text-stone-600 mb-8 leading-relaxed flex-grow">
                Gennem struktureret leg, nærvær og fælles grin hjælper jeg jer med at genfinde den tætte kontakt. Theraplay er en hjertevarm familieterapi til børn (0-12 år), der styrker båndet og tilliden – særligt ved tilknytningsforstyrrelser, traumer, autisme eller ADHD.
              </p>
              <Link to="/theraplay" className="bg-stone-900 text-white px-8 py-3 rounded-full font-medium inline-block hover:bg-stone-800 transition-all hover:-translate-y-1 w-full">
                Læs mere
              </Link>
            </motion.div>

            {/* Marte Meo */}
            <motion.div
              id="marte-meo"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-center text-center p-4 scroll-mt-24"
            >
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-6">
                <Video className="w-8 h-8 text-stone-800" />
              </div>
              <h3 className="text-2xl font-light text-stone-900 mb-4 uppercase">Marte Meo</h3>
              <p className="text-stone-600 mb-8 leading-relaxed flex-grow">
                Ved at se på korte videooptagelser af jeres hverdagsstunder, hjælper jeg dig med at få øje på alt det, der allerede lykkes. Vi finder de gyldne øjeblikke og omsætter dem til konkrete, kærlige handlinger, der styrker jeres samspil og kommunikation.
              </p>
              <Link to="/marte-meo" className="bg-stone-900 text-white px-8 py-3 rounded-full font-medium inline-block hover:bg-stone-800 transition-all hover:-translate-y-1 w-full">
                Læs mere
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Watermark Overlay - Winding Road */}
        <div className="absolute inset-0 z-20 pointer-events-none opacity-[0.08] flex items-start justify-center">
          <img 
            src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/95574613-b9ad-4e75-84fb-52de2d444fa1.png" 
            alt="" 
            className="w-full h-full object-cover scale-150 rotate-6 mix-blend-multiply object-top"
          />
        </div>

        {/* Wave Divider at bottom of Services */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-30">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-white"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      {/* Intro Section - Updated to follow container lines */}
      <section className="bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16 py-16 lg:py-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-3xl md:text-4xl font-light text-stone-900 mb-6 uppercase">
                Mød mig og min pædagogiske tilgang
              </h2>
              <div className="prose prose-lg text-stone-600">
                <p>
                  Bag Vejen til barnet står 25 års engagement, faglighed og praktisk erfaring fra den pædagogiske verden. Gennem et kvart århundrede har jeg arbejdet tæt sammen med børn, forældre og pædagogiske fagfolk.
                </p>
                <h3 className="text-xl font-semibold text-stone-900 mt-6 mb-3">
                  Omsorgen som udgangspunkt
                </h3>
                <p>
                  I mit pædagogiske arbejde er omsorgen altid udgangspunktet. Jeg møder både barnet, forældrene og fagfolk med dyb forståelse. Mit fokus er at skabe et trygt rum, hvor alle føler sig set og hørt. Når vi finder forståelsen for barnets signaler, forældrenes intentioner og fagfolkenes perspektiver, sker der noget særligt: I genfinder forbindelsen til hinanden. Omsorg handler om at styrke de bånd og det samarbejde, der holder sammen på hverdagen omkring barnet.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/hvem-er-jeg" className="text-stone-800 font-semibold flex items-center gap-2 hover:gap-3 hover:-translate-y-0.5 transition-all">
                  Læs mere om min baggrund <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 relative w-full"
            >
              <img 
                src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/b62eeb3e-b7f7-4b4d-b13b-c0ddcce81d17.jpg" 
                alt="Anette Lundager Schädler" 
                className="w-full h-auto object-cover shadow-2xl"
              />
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm p-4 md:p-6 border border-white/20 shadow-xl rounded-lg">
                <div className="flex items-center gap-4">
                  <Heart className="w-6 h-6 md:w-8 md:h-8 text-stone-900" />
                  <p className="text-stone-900 font-semibold text-sm md:text-base">Bag Vejen til barnet står 25 års engagement</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
